import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, X, MapPin, Building2, Trees } from 'lucide-react';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import api from '../utils/api';
import Pagination from '../components/Pagination';
import SearchBar from '../components/SearchBar';
import { showSuccess, showError } from '../utils/toast';
import { isUserLoggedIn } from '../utils/auth';
import BusinessCard from '../components/BusinessCard';

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [villagesByBlock, setVillagesByBlock] = useState({});
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const [showFilter, setShowFilter] = useState(false);

  const searchQuery = searchParams.get('q') || '';
  const selectedCategory = searchParams.get('category') || '';
  const selectedBlock = searchParams.get('block') || '';
  const rawCity = searchParams.get('city') || '';
  const rawVillage = searchParams.get('village') || '';
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const tagsParam = searchParams.get('tags') || '';
  const selectedTags = useMemo(() => (tagsParam ? tagsParam.split(',').filter(Boolean) : []), [tagsParam]);
  const selectedSort = searchParams.get('sort') || 'top-rated';

  // Extract all blocks and all villages
  const blocks = useMemo(() => Object.keys(villagesByBlock).sort(), [villagesByBlock]);

  const allVillages = useMemo(() => {
    return Object.values(villagesByBlock)
      .flat()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [villagesByBlock]);

  // Villages for currently selected block
  const blockVillages = useMemo(() => {
    if (!selectedBlock) return [];
    return (villagesByBlock[selectedBlock] || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [selectedBlock, villagesByBlock]);

  // Find matched city
  const matchedCity = useMemo(() => {
    if (!rawCity || rawCity === 'all') return null;
    return cities.find(
      (c) => c._id === rawCity || c.slug === rawCity.toLowerCase() || c.name?.toLowerCase() === rawCity.toLowerCase()
    );
  }, [cities, rawCity]);

  // Find matched village
  const matchedVillage = useMemo(() => {
    if (!rawVillage || rawVillage === 'all') return null;
    return allVillages.find(
      (v) => v._id === rawVillage || v.slug === rawVillage.toLowerCase()
    );
  }, [allVillages, rawVillage]);

  // Matching city for the currently selected block (e.g. Tohana block -> Tohana city)
  const matchingCityForBlock = useMemo(() => {
    if (!selectedBlock) return null;
    const prefix = selectedBlock.toLowerCase().split(' ')[0];
    return cities.find((c) => c.name.toLowerCase().includes(prefix));
  }, [cities, selectedBlock]);

  // Determine current City/Village dropdown value
  const locationSelectValue = useMemo(() => {
    if (matchedVillage) return `village:${matchedVillage._id}`;
    if (rawVillage) return `village:${rawVillage}`;
    if (matchedCity) return `city:${matchedCity._id}`;
    if (rawCity && rawCity !== 'all') return `city:${rawCity}`;
    return 'all';
  }, [matchedVillage, rawVillage, matchedCity, rawCity]);

  const hasActiveFilters = Boolean(
    selectedCategory ||
    (selectedBlock && selectedBlock !== 'all') ||
    (rawCity && rawCity !== 'all') ||
    rawVillage ||
    selectedTags.length > 0 ||
    selectedSort !== 'top-rated'
  );

  useEffect(() => {
    fetchCategories();
    fetchCities();
    fetchVillagesByBlock();
  }, []);

  useEffect(() => {
    if (!isUserLoggedIn()) return;
    api.get('/favourites').then((r) => {
      const list = r.data?.favourites || [];
      setFavouriteIds(new Set(list.map((b) => (b._id || b).toString())));
    }).catch(() => { });
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const fetchSearchResults = async () => {
      try {
        setLoading(true);
        const params = {
          page: currentPage,
          limit: 20
        };

        if (searchQuery.trim()) {
          params.search = searchQuery.trim();
        }

        if (selectedCategory) {
          params.category = selectedCategory;
        }

        // Location filtering hierarchy: Specific Village > Specific City > Entire Block > All
        if (rawVillage) {
          params.village = matchedVillage ? matchedVillage._id : rawVillage;
        } else if (rawCity && rawCity !== 'all') {
          params.city = matchedCity ? matchedCity._id : rawCity;
        } else if (selectedBlock && selectedBlock !== 'all') {
          params.block = selectedBlock;
          params.city = 'all';
        } else {
          params.city = 'all';
        }

        if (selectedTags.length > 0) {
          params.tags = selectedTags.join(',');
        }

        params.sort = selectedSort;

        const response = await api.get('/businesses', { params });

        if (isCancelled) return;

        if (response.data && response.data.businesses) {
          let results = response.data.businesses;

          // Filter by tags if selected (client-side filtering)
          if (selectedTags.length > 0) {
            results = results.filter(business => {
              if (!business.tags || business.tags.length === 0) return false;
              const businessTagIds = business.tags.map(tag =>
                typeof tag === 'object' ? tag._id : tag
              );
              return selectedTags.some(selectedTagId =>
                businessTagIds.includes(selectedTagId)
              );
            });
          }

          setBusinesses(results);
          setPagination(response.data.pagination);
        } else {
          setBusinesses([]);
          setPagination(null);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error fetching search results:', error);
          setBusinesses([]);
          setPagination(null);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchSearchResults();

    return () => {
      isCancelled = true;
    };
  }, [searchQuery, currentPage, selectedCategory, rawCity, rawVillage, selectedBlock, tagsParam, selectedSort, matchedCity, matchedVillage, selectedTags]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await api.get('/cities');
      setCities(response.data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchVillagesByBlock = async () => {
    try {
      const response = await api.get('/villages/by-block');
      setVillagesByBlock(response.data || {});
    } catch (error) {
      console.error('Error fetching villages by block:', error);
    }
  };

  const handleSearch = (term) => {
    const newParams = new URLSearchParams(searchParams);
    if (term && term.trim()) {
      newParams.set('q', term.trim());
    } else {
      newParams.delete('q');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const handleFilterChange = (filterType, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set(filterType, value);
    } else {
      newParams.delete(filterType);
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Block change handler: cascades to clear specific village/city
  const handleBlockChange = (newBlock) => {
    const newParams = new URLSearchParams(searchParams);
    if (newBlock && newBlock !== 'all') {
      newParams.set('block', newBlock);
    } else {
      newParams.delete('block');
    }
    // When changing block, clear previous city & village to avoid invalid combinations
    newParams.delete('city');
    newParams.delete('village');
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // City or Village change handler
  const handleLocationChange = (val) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');

    if (!val || val === 'all') {
      newParams.delete('city');
      newParams.delete('village');
    } else if (val.startsWith('city:')) {
      const cityId = val.replace('city:', '');
      newParams.set('city', cityId);
      newParams.delete('village');

      // Auto-set matching block if no block was chosen
      if (!selectedBlock) {
        const c = cities.find(ct => ct._id === cityId);
        if (c) {
          const matchB = blocks.find(b =>
            b.toLowerCase().includes(c.name.toLowerCase().split(' ')[0]) ||
            c.name.toLowerCase().includes(b.toLowerCase().split(' ')[0])
          );
          if (matchB) newParams.set('block', matchB);
        }
      }
    } else if (val.startsWith('village:')) {
      const villageId = val.replace('village:', '');
      newParams.set('village', villageId);
      newParams.delete('city');

      // Auto-set the village's block if no block was chosen
      if (!selectedBlock) {
        const v = allVillages.find(vl => vl._id === villageId);
        if (v && v.block) {
          newParams.set('block', v.block);
        }
      }
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page);
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    const newParams = new URLSearchParams();
    if (searchQuery) newParams.set('q', searchQuery);
    setSearchParams(newParams);
  };

  const handleAddFavourite = async (id) => {
    try {
      await api.post(`/favourites/${id}`);
      setFavouriteIds((prev) => new Set([...prev, id.toString()]));
      showSuccess('Added to favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not add to favourites');
    }
  };

  const handleRemoveFavourite = async (id) => {
    try {
      await api.delete(`/favourites/${id}`);
      setFavouriteIds((prev) => {
        const next = new Set(prev);
        next.delete(id.toString());
        return next;
      });
      showSuccess('Removed from favourites');
    } catch (e) {
      showError(e.response?.data?.message || 'Could not remove from favourites');
    }
  };

  // SEO Page Title calculation
  let pageTitle = 'Search Businesses - Digital Fatehabad';
  if (searchQuery) {
    pageTitle = `Search "${searchQuery}"`;
    if (selectedBlock) pageTitle += ` in ${selectedBlock} Block`;
    if (matchedVillage) pageTitle += ` (${matchedVillage.name})`;
    pageTitle += ' - Digital Fatehabad';
  } else if (selectedBlock) {
    pageTitle = `Businesses in ${selectedBlock} Block`;
    if (matchedVillage) pageTitle += ` - ${matchedVillage.name}`;
    pageTitle += ' - Digital Fatehabad';
  }

  return (
    <>
      <SEO
        title={pageTitle}
        description={searchQuery
          ? `Browse search results for "${searchQuery}" in Fatehabad. Find local businesses, contact details, and reviews.`
          : 'Search for local businesses, services, and shops across Fatehabad district, blocks, cities, and villages.'}
        canonical="/search"
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Search Bar with Filter Toggle */}
          <div className="mb-6 max-w-2xl mx-auto">
            <div className="flex gap-2 items-center min-w-0">
              <div className="flex-1 min-w-0">
                <SearchBar
                  placeholder="Search businesses, services, locations, villages..."
                  className="w-full"
                  onSearch={handleSearch}
                />
              </div>
              <button 
                type="button"
                onClick={() => setShowFilter(!showFilter)}
                className={`flex-shrink-0 px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 flex items-center gap-1.5 shadow-sm active:scale-95 ${
                  showFilter || hasActiveFilters
                    ? 'bg-primary-600 text-white ring-2 ring-primary-400/40'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
                }`}
                title="Filter Results by Block, City, Village & Category"
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span>Filters</span>
                {hasActiveFilters && (
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse ml-0.5" />
                )}
              </button>
            </div>
          </div>

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <div className="mb-5 max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-2 animate-fade-in">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                Active Filters:
              </span>

              {selectedCategory && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary-100 text-primary-800 dark:bg-primary-950/70 dark:text-primary-300 border border-primary-200 dark:border-primary-800 shadow-2xs">
                  📁 {categories.find(c => c._id === selectedCategory)?.name || 'Category'}
                  <button 
                    onClick={() => handleFilterChange('category', '')} 
                    className="hover:text-red-500 ml-0.5"
                    title="Remove category filter"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {selectedBlock && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 shadow-2xs">
                  <Trees className="w-3 h-3" /> Block: {selectedBlock}
                  <button 
                    onClick={() => handleBlockChange('')} 
                    className="hover:text-red-500 ml-0.5"
                    title="Remove block filter"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {matchedCity && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 dark:bg-blue-950/70 dark:text-blue-300 border border-blue-200 dark:border-blue-800 shadow-2xs">
                  <Building2 className="w-3 h-3" /> City: {matchedCity.name}
                  <button 
                    onClick={() => handleFilterChange('city', '')} 
                    className="hover:text-red-500 ml-0.5"
                    title="Remove city filter"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {matchedVillage && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border border-amber-200 dark:border-amber-800 shadow-2xs">
                  <MapPin className="w-3 h-3" /> Village: {matchedVillage.name}
                  <button 
                    onClick={() => handleFilterChange('village', '')} 
                    className="hover:text-red-500 ml-0.5"
                    title="Remove village filter"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              {selectedSort !== 'top-rated' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800 dark:bg-purple-950/70 dark:text-purple-300 border border-purple-200 dark:border-purple-800 shadow-2xs">
                  Sort: {selectedSort}
                  <button 
                    onClick={() => handleFilterChange('sort', 'top-rated')} 
                    className="hover:text-red-500 ml-0.5"
                    title="Reset sort to Top Rated"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}

              <button
                onClick={handleClearFilters}
                className="text-xs text-red-600 dark:text-red-400 font-bold hover:underline px-2 py-1"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Filters Box - Sequential: Block -> City / Village */}
          {showFilter && (
            <div className="mb-7 max-w-4xl mx-auto p-4 sm:p-5 bg-white dark:bg-gray-850 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-md animate-fade-in">
              <div className="flex items-center justify-between mb-4 pb-2.5 border-b border-gray-100 dark:border-gray-700/60">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary-500" />
                  <span className="text-xs sm:text-sm font-extrabold uppercase tracking-wide text-gray-800 dark:text-gray-200">
                    फ़िल्टर सेटिंग्स (Filter By Block, Location & Category)
                  </span>
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={handleClearFilters}
                    className="text-xs font-bold text-red-600 hover:text-red-700 dark:text-red-400 transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                
                {/* 1. Category */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-300 mb-1.5">
                    📂 Category (श्रेणी)
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium"
                  >
                    <option value="">All Categories (सभी श्रेणियां)</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 2. Step 1: Block Filter */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                    <span>🏛️ 1. Block (ब्लॉक)</span>
                    {selectedBlock && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold uppercase">चयनित</span>
                    )}
                  </label>
                  <select
                    value={selectedBlock}
                    onChange={(e) => handleBlockChange(e.target.value)}
                    className={`w-full px-3 py-2 text-xs sm:text-sm border rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium ${
                      selectedBlock
                        ? 'border-emerald-500 bg-emerald-50/20 dark:bg-emerald-950/20 font-bold'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    <option value="">All Blocks (सभी 7 ब्लॉक)</option>
                    {blocks.map((b) => (
                      <option key={b} value={b}>
                        {b} Block ({villagesByBlock[b]?.length || 0} गाँव)
                      </option>
                    ))}
                  </select>
                </div>

                {/* 3. Step 2: City / Village Filter (Cascades with Block) */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                    <span>📍 2. City / Village (शहर / गाँव)</span>
                    {locationSelectValue !== 'all' && (
                      <span className="text-[10px] text-primary-600 dark:text-primary-400 font-bold uppercase">चयनित</span>
                    )}
                  </label>
                  <select
                    value={locationSelectValue}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    className={`w-full px-3 py-2 text-xs sm:text-sm border rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium ${
                      locationSelectValue !== 'all'
                        ? 'border-primary-500 bg-primary-50/20 dark:bg-primary-950/20 font-bold'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {selectedBlock ? (
                      <>
                        <option value="all">
                          📍 All in {selectedBlock} Block ({blockVillages.length + (matchingCityForBlock ? 1 : 0)} क्षेत्र)
                        </option>
                        {matchingCityForBlock && (
                          <optgroup label="🏙️ Main City (मुख्य शहर)">
                            <option value={`city:${matchingCityForBlock._id}`}>
                              🏙️ {matchingCityForBlock.name} (City)
                            </option>
                          </optgroup>
                        )}
                        {blockVillages.length > 0 && (
                          <optgroup label={`🌾 ${selectedBlock} Block के गाँव (${blockVillages.length})`}>
                            {blockVillages.map((v) => (
                              <option key={v._id} value={`village:${v._id}`}>
                                🌾 {v.name} {v.pincode ? `(${v.pincode})` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </>
                    ) : (
                      <>
                        <option value="all">📍 All Cities & Villages (सभी शहर व गाँव)</option>
                        <optgroup label="🏙️ Cities (शहर)">
                          {cities.map((c) => (
                            <option key={c._id} value={`city:${c._id}`}>
                              🏙️ {c.name}
                            </option>
                          ))}
                        </optgroup>
                        {allVillages.length > 0 && (
                          <optgroup label="🌾 Villages (गाँव)">
                            {allVillages.map((v) => (
                              <option key={v._id} value={`village:${v._id}`}>
                                🌾 {v.name} ({v.block})
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </>
                    )}
                  </select>
                </div>

                {/* 4. Sort By */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-300 mb-1.5">
                    ⚡ Sort By (क्रमबद्ध करें)
                  </label>
                  <select
                    value={selectedSort}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                    className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium"
                  >
                    <option value="top-rated">⭐ Top Rated</option>
                    <option value="createdAt:desc">✨ Newest First</option>
                    <option value="name:asc">🔤 Name (A - Z)</option>
                    <option value="name:desc">🔤 Name (Z - A)</option>
                  </select>
                </div>

              </div>
            </div>
          )}

          {/* Smart Intent Match Cards */}
          {/blood|donor|rakt|khoon/i.test(searchQuery) && (
            <div className="mb-6 p-4 sm:p-5 bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white rounded-2xl shadow-md flex flex-col sm:flex-row items-center justify-between gap-4 border border-red-400/30">
              <div className="flex items-center gap-3.5 text-center sm:text-left">
                <span className="text-3xl sm:text-4xl flex-shrink-0">🩸</span>
                <div>
                  <h4 className="font-bold text-base sm:text-lg">Looking for Blood Donors in Fatehabad?</h4>
                  <p className="text-xs sm:text-sm text-red-100 mt-0.5">We have 120+ verified emergency donors across A+, B+, O+, AB+ groups in Fatehabad district.</p>
                </div>
              </div>
              <Link to="/blood-donors" className="w-full sm:w-auto text-center px-5 py-2.5 bg-white text-red-700 font-bold rounded-xl text-xs sm:text-sm hover:bg-red-50 transition-all shadow-sm shrink-0 whitespace-nowrap active:scale-95">
                View Blood Donors Directory →
              </Link>
            </div>
          )}

          {/officer|officials|dc|sp|dsp|police|helpline|tehsildar|sdo|administration/i.test(searchQuery) && (
            <div className="mb-6 p-4 sm:p-5 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 text-white rounded-2xl shadow-md flex flex-col sm:flex-row items-center justify-between gap-4 border border-blue-400/30">
              <div className="flex items-center gap-3.5 text-center sm:text-left">
                <span className="text-3xl sm:text-4xl flex-shrink-0">👮</span>
                <div>
                  <h4 className="font-bold text-base sm:text-lg">Looking for Government Officers Directory?</h4>
                  <p className="text-xs sm:text-sm text-blue-100 mt-0.5">210+ verified official phone numbers of district administration, police & departments.</p>
                </div>
              </div>
              <Link to="/officers" className="w-full sm:w-auto text-center px-5 py-2.5 bg-white text-blue-700 font-bold rounded-xl text-xs sm:text-sm hover:bg-blue-50 transition-all shadow-sm shrink-0 whitespace-nowrap active:scale-95">
                View Officers Directory →
              </Link>
            </div>
          )}

          {/bus|timetable|roadways|bus stand|timing|bus route/i.test(searchQuery) && (
            <div className="mb-6 p-4 sm:p-5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white rounded-2xl shadow-md flex flex-col sm:flex-row items-center justify-between gap-4 border border-emerald-400/30">
              <div className="flex items-center gap-3.5 text-center sm:text-left">
                <span className="text-3xl sm:text-4xl flex-shrink-0">🚌</span>
                <div>
                  <h4 className="font-bold text-base sm:text-lg">Looking for Haryana Roadways Bus Timetable?</h4>
                  <p className="text-xs sm:text-sm text-emerald-100 mt-0.5">Complete Haryana Roadways timetable for Fatehabad, Tohana & through bus routes.</p>
                </div>
              </div>
              <Link to="/bus-timetable" className="w-full sm:w-auto text-center px-5 py-2.5 bg-white text-emerald-700 font-bold rounded-xl text-xs sm:text-sm hover:bg-emerald-50 transition-all shadow-sm shrink-0 whitespace-nowrap active:scale-95">
                Check Bus Timetable →
              </Link>
            </div>
          )}

          {/village|gao|gaon|sarpanch/i.test(searchQuery) && (
            <div className="mb-6 p-4 sm:p-5 bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white rounded-2xl shadow-md flex flex-col sm:flex-row items-center justify-between gap-4 border border-amber-400/30">
              <div className="flex items-center gap-3.5 text-center sm:text-left">
                <span className="text-3xl sm:text-4xl flex-shrink-0">📍</span>
                <div>
                  <h4 className="font-bold text-base sm:text-lg">Looking for Villages in Fatehabad?</h4>
                  <p className="text-xs sm:text-sm text-amber-100 mt-0.5">Explore 274 villages across all 7 blocks with Sarpanch, schools & demographics.</p>
                </div>
              </div>
              <Link to="/villages" className="w-full sm:w-auto text-center px-5 py-2.5 bg-white text-amber-700 font-bold rounded-xl text-xs sm:text-sm hover:bg-amber-50 transition-all shadow-sm shrink-0 whitespace-nowrap active:scale-95">
                View All 274 Villages →
              </Link>
            </div>
          )}

          {/* Results */}
          {loading ? (
            <PageLoader message="Searching..." className="py-12" />
          ) : businesses.length > 0 ? (
            <>
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400 flex flex-wrap items-center justify-between gap-2">
                <div>
                  Found <strong className="text-gray-900 dark:text-white font-bold">{pagination?.totalItems || pagination?.total || businesses.length}</strong> result{((pagination?.totalItems || pagination?.total || businesses.length) !== 1) ? 's' : ''} {searchQuery ? `for "${searchQuery}"` : ''}
                  {selectedBlock && <span className="ml-1 text-emerald-600 dark:text-emerald-400 font-semibold">• {selectedBlock} Block</span>}
                  {matchedCity && <span className="ml-1 text-blue-600 dark:text-blue-400 font-semibold">• {matchedCity.name} City</span>}
                  {matchedVillage && <span className="ml-1 text-amber-600 dark:text-amber-400 font-semibold">• {matchedVillage.name}</span>}
                </div>
                {!showFilter && (
                  <button
                    onClick={() => setShowFilter(true)}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-semibold flex items-center gap-1"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" /> Adjust location or filters
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-3 gap-0 md:gap-6 divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-800 mb-8">
                {businesses.map((business) => (
                  <BusinessCard
                    key={business._id}
                    business={business}
                    favouriteIds={favouriteIds}
                    onAddFavourite={handleAddFavourite}
                    onRemoveFavourite={handleRemoveFavourite}
                  />
                ))}
              </div>

              {pagination && pagination.totalPages > 1 && (
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No businesses found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {searchQuery || hasActiveFilters
                  ? 'Try adjusting your search terms, block, or location filters.'
                  : 'Try searching for something else.'}
              </p>
              {hasActiveFilters && (
                <div className="mt-6">
                  <button
                    onClick={handleClearFilters}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-xl text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchResults;
